<template>
  <v-stepper v-model="step" :vertical="vertical" :class="cardColor">
    <v-stepper-step :color="mainColor" :complete="step > 1" step="1">
      {{ $t('general.enterTheNameAndDescriptionOfTheTask') }}
    </v-stepper-step>

    <v-stepper-content step="1">
      <v-card class="mb-6" :color="cardColor">
        <v-text-field color="kbDarkBlueText"
                      :background-color="inputsBackgroundColor"
                      class="mb-6"
                      hide-details
                      :label="$t('general.insertANewTask')"
                      solo
                      v-model="newTask.name"/>

        <v-combobox :items="typesOfTask"
                    :background-color="inputsBackgroundColor"
                    item-text="type"
                    item-value="type"
                    :label="$t('general.type')"
                    :placeholder="$t('general.type')"
                    :return-object="false"
                    solo
                    v-model="newTask.type">
        </v-combobox>

        <v-textarea color="kbDarkBlueText"
                    :background-color="inputsBackgroundColor"
                    height="80px"
                    hide-details
                    :label="$t('general.observations')"
                    solo
                    v-model="newTask.observation"/>
      </v-card>
      <div class="d-flex justify-end align-center">
        <v-btn color="kbRed"
               class="mr-2"
               outlined
               @click="toggleAddNewTaskComponent()">
          {{ $t('general.cancel') }}
        </v-btn>
        <v-btn :color="mainColor"
               dark
               @click="changeStep(2)">
          {{ $t('general.continue') }}
        </v-btn>
      </div>
    </v-stepper-content>

    <v-stepper-step :color="mainColor"
                    :complete="step > 2" step="2">
      {{ $t('general.selectFinishingDate') }}
    </v-stepper-step>

    <v-stepper-content step="2">
      <v-card :color="cardColor" class="mb-6">
        <v-date-picker
          :color="mainColor"
          v-model="newTask.date"
          full-width
          :landscape="$vuetify.breakpoint.mdAndUp"
        />
      </v-card>
      <div class="d-flex justify-end align-center">
        <v-btn color="kbGray"
               class="mr-2"
               outlined
               @click="changeStep(1)">
          {{ $t('general.goBack') }}
        </v-btn>
        <v-btn :color="mainColor"
               dark
               @click="changeStep(3)">
          {{ $t('general.continue') }}
        </v-btn>
      </div>
    </v-stepper-content>

    <v-stepper-step :color="mainColor"
                    :complete="step > 3"
                    step="3">
      {{ $t('general.selectFinishingHour') }}
    </v-stepper-step>

    <v-stepper-content step="3">
      <v-card color="kbWhite" class="mb-6">
        <v-time-picker
          format="24hr"
          :color="mainColor"
          v-model="newTask.time"
          :landscape="$vuetify.breakpoint.mdAndUp"
          full-width
        />
      </v-card>
      <div class="d-flex justify-end align-center">
        <v-btn color="kbGray"
               class="mr-2"
               outlined
               @click="changeStep(2)">
          {{ $t('general.goBack') }}
        </v-btn>
        <v-btn :color="mainColor"
               dark
               :loading="creatingTask"
               @click="addNewTask()">
          {{ extraStep ? $t('general.continue') : $t('general.createTask') }}
        </v-btn>
      </div>
    </v-stepper-content>

    <v-stepper-step :color="mainColor"
                    step="4"
                    v-if="extraStep">
      {{ $t('general.taskCreatedSuccessfully') }}
    </v-stepper-step>

    <v-stepper-content step="4" v-if="extraStep">
      <v-card color="kbWhite" class="mb-6" flat>
        <v-row>
          <v-col cols="12" class="d-flex flex-column justify-center align-center">
            <span class="kbDarkBlueText--text text-capitalize"
                  style="font-size: 26px">
              {{ newTask.name }}
            </span>
            <p class="kbDarkBlueText--text pt-3" style="max-width: 50%">
              {{ newTask.observation || 'Sin observaciones' }}
            </p>
            <div class="d-flex justify-center align-center">
              <div class="d-flex align-center mr-3">
                <v-icon color="kbDarkBlueText"
                        class="mr-1"
                        size="32px">mdi-calendar</v-icon>
                <span class="kbDarkBlueText--text" style="font-size: 18px">
                  {{ newTask.date }}
                </span>
              </div>
              <div class="d-flex align-center ml-3">
                <v-icon color="kbDarkBlueText"
                        class="mr-1"
                        size="32px">mdi-clock</v-icon>
                <span class="kbDarkBlueText--text" style="font-size: 18px">
                  {{ newTask.time }}
                </span>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
      <div class="d-flex justify-end align-center">
        <v-btn color="kbGray"
               class="mr-2"
               outlined
               @click="changeStep(3)">
          {{ $t('general.goBack') }}
        </v-btn>
      </div>
    </v-stepper-content>

    <v-col cols="12"
           class="pt-6 pl-6"
           v-if="title">
      <span class="kbDarkBlueText--text"
            style="font-size: 24px">
        {{ title }}
      </span>
    </v-col>
  </v-stepper>
</template>

<script>
import notifications from '@/libs/notifications'
import { mapGetters } from 'vuex'
import TimeZones from '@/mixins/TimeZones'
export default {
  computed: {
    ...mapGetters(['appUuid', 'companyUuid'])
  },
  data () {
    return {
      creatingTask: false,
      newTask: {
        name: '',
        date: '',
        sendEmailNotificationTo: '',
        sendPhoneNotificationTo: '',
        observation: '',
        time: '',
        extraData: [],
        done: false,
        reportToUser: false,
        type: ''
      },
      step: 1
    }
  },
  methods: {
    async addNewTask () {
      if (!this.newTask.time) return notifications.error(this.$t('general.selectFinishingHour'))
      try {
        const { date, time, ...all } = this.newTask
        let fullDate = this.convertTZToUserTZ(`${date} ${time}`, this.userKeybeTimeZone.label)
        fullDate = this.convertTZ(fullDate)
        if (this.extraStep) {
          const newTask = { date: fullDate, ...all }
          this.$emit('onNewTaskSaved', newTask)
          this.changeStep(4)
          return
        }
        if (this.creatingTask) return
        this.creatingTask = true

        const data = {
          companyId: this.companyUuid,
          appId: this.appUuid,
          userId: this.userId,
          data: { date: fullDate, ...all }
        }

        await this.$store.dispatch('newUserTask', data)
        this.creatingTask = false
        this.$emit('updateTypes')
        this.toggleAddNewTaskComponent()
        this.$emit('getTasks')
      } catch (e) {
        this.creatingTask = false
        notifications.error(e)
        throw e
      }
    },
    changeStep (step) {
      if (this.step === 1 && step === 2 && !this.newTask.name.trim()) return notifications.error(this.$t('general.youMustAddTheNameOfTheTask'))
      if (this.step === 1 && step === 2) {
        if (this.userTasks && Array.isArray(this.userTasks) && this.userTasks.length > 0) {
          const existingTask = this.userTasks.find(
            task => task.name.toLowerCase() === this.newTask.name.toLowerCase()
          )
          if (existingTask) return notifications.error(this.$t('general.sameNameTask'))
        }
      } else if (step === 3 && !this.newTask.date) return notifications.error(this.$t('general.selectFinishingDate'))
      this.step = step
    },
    toggleAddNewTaskComponent () {
      this.$emit('toggleAddNewTaskComponent')
    }
  },
  mixins: [TimeZones],
  props: {
    cardColor: {
      type: String,
      default: 'kbLight'
    },
    inputsBackgroundColor: {
      type: String,
      default: 'kbWhite'
    },
    extraStep: {
      type: Boolean,
      default: false
    },
    mainColor: {
      type: String,
      default: 'kbPink'
    },
    title: {
      type: String,
      default: ''
    },
    userId: {
      type: String,
      default: ''
    },
    userTasks: {
      type: Array,
      default: () => []
    },
    typesOfTask: {
      type: Array,
      default: () => []
    },
    vertical: {
      type: Boolean,
      default: true
    }
  }
}
</script>
